import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, User, Calendar, Target, TrendingUp,
  Activity, CheckCircle2, AlertTriangle, AlertCircle, Clock,
  BarChart2, ChevronRight, Flame, Layers, GitFork, ArrowRightLeft,
  PieChart as PieIcon
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { patientsAPI } from '../services/api';
import { formatNumber } from '../utils/numberFormat';
import { calculateRouteSplits, getRouteClinicalBadge } from '../utils/routeClassification';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, Cell
} from 'recharts';

export default function PatientProfile() {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [macroView, setMacroView] = useState('grams'); // 'grams' | 'percentage'
  const [routeView, setRouteView] = useState('calories'); // 'calories' | 'percentage'

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser(payload);
      } catch (e) {}
    }
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [patientData, patientRecords] = await Promise.all([
        patientsAPI.getById(patientId),
        patientsAPI.getRecords(patientId),
      ]);
      setPatient(patientData);
      setRecords(patientRecords || []);
    } catch (err) {
      console.error('Failed to load patient profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = React.useMemo(() => {
    if (!records.length) return null;
    const sorted = [...records].sort((a, b) => new Date(a.date) - new Date(b.date));
    const percentages = sorted.map(r =>
      r.target_energy_kcal > 0 ? (r.grand_totals.energy_kcal / r.target_energy_kcal) * 100 : 0
    );
    const avgPct = percentages.reduce((a, b) => a + b, 0) / percentages.length;
    const avgEnergy = sorted.reduce((a, r) => a + r.grand_totals.energy_kcal, 0) / sorted.length;
    const avgProtein = sorted.reduce((a, r) => a + (r.grand_totals?.protein_g || 0), 0) / sorted.length;
    const avgCarbs = sorted.reduce((a, r) => a + (r.grand_totals?.carbohydrate_g || 0), 0) / sorted.length;
    const avgFat = sorted.reduce((a, r) => a + (r.grand_totals?.fat_g || 0), 0) / sorted.length;
    const avgProteinPct = sorted.reduce((a, r) => a + (parseFloat(r.grand_totals?.percentage_protein) || 0), 0) / sorted.length;
    const avgCarbsPct = sorted.reduce((a, r) => a + (parseFloat(r.grand_totals?.percentage_carbs) || 0), 0) / sorted.length;
    const avgFatPct = sorted.reduce((a, r) => a + (parseFloat(r.grand_totals?.percentage_fat) || 0), 0) / sorted.length;
    const adequateDays = percentages.filter(p => p >= 80 && p <= 120).length;
    const overfeedingDays = percentages.filter(p => p > 130).length;
    const deficitDays = percentages.filter(p => p < 50).length;

    // Route calculations
    const routeSplitsList = sorted.map(r => calculateRouteSplits(r.oral_enteral_items, r.parenteral_items));
    const avgOralKcal = routeSplitsList.reduce((a, s) => a + s.oralKcal, 0) / sorted.length;
    const avgEnteralKcal = routeSplitsList.reduce((a, s) => a + s.enteralKcal, 0) / sorted.length;
    const avgParenteralKcal = routeSplitsList.reduce((a, s) => a + s.parenteralKcal, 0) / sorted.length;

    const totalOralKcal = routeSplitsList.reduce((a, s) => a + s.oralKcal, 0);
    const totalEnteralKcal = routeSplitsList.reduce((a, s) => a + s.enteralKcal, 0);
    const totalParenteralKcal = routeSplitsList.reduce((a, s) => a + s.parenteralKcal, 0);
    const cumulativeTotalKcal = totalOralKcal + totalEnteralKcal + totalParenteralKcal;

    const cumOralPct = cumulativeTotalKcal > 0 ? (totalOralKcal / cumulativeTotalKcal) * 100 : 0;
    const cumEnteralPct = cumulativeTotalKcal > 0 ? (totalEnteralKcal / cumulativeTotalKcal) * 100 : 0;
    const cumParenteralPct = cumulativeTotalKcal > 0 ? (totalParenteralKcal / cumulativeTotalKcal) * 100 : 0;

    const latestSplit = routeSplitsList[routeSplitsList.length - 1] || { oralPct: 0, enteralPct: 0, parenteralPct: 0 };
    const latestRouteBadge = getRouteClinicalBadge(latestSplit.oralPct, latestSplit.enteralPct, latestSplit.parenteralPct);

    return {
      totalRecords: records.length,
      avgPct: avgPct.toFixed(1),
      avgEnergy: Math.round(avgEnergy),
      avgProtein: avgProtein.toFixed(1),
      avgCarbs: avgCarbs.toFixed(1),
      avgFat: avgFat.toFixed(1),
      avgProteinPct: avgProteinPct.toFixed(1),
      avgCarbsPct: avgCarbsPct.toFixed(1),
      avgFatPct: avgFatPct.toFixed(1),
      adequateDays,
      overfeedingDays,
      deficitDays,
      // Route stats
      avgOralKcal: Math.round(avgOralKcal),
      avgEnteralKcal: Math.round(avgEnteralKcal),
      avgParenteralKcal: Math.round(avgParenteralKcal),
      cumOralPct: parseFloat(cumOralPct.toFixed(1)),
      cumEnteralPct: parseFloat(cumEnteralPct.toFixed(1)),
      cumParenteralPct: parseFloat(cumParenteralPct.toFixed(1)),
      totalOralKcal,
      totalEnteralKcal,
      totalParenteralKcal,
      latestRouteBadge,
    };
  }, [records]);

  const chartData = React.useMemo(() => {
    return [...records]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(r => {
        const d = new Date(r.date);
        const pct = r.target_energy_kcal > 0
          ? Math.round((r.grand_totals.energy_kcal / r.target_energy_kcal) * 100)
          : 0;
        const routeSplits = calculateRouteSplits(r.oral_enteral_items, r.parenteral_items);
        const routeBadge = getRouteClinicalBadge(routeSplits.oralPct, routeSplits.enteralPct, routeSplits.parenteralPct);
        return {
          date: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
          fullDate: d.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }),
          energy: Math.round(r.grand_totals?.energy_kcal || 0),
          target: r.target_energy_kcal,
          pct,
          protein: parseFloat((r.grand_totals?.protein_g || 0).toFixed(1)),
          carbs: parseFloat((r.grand_totals?.carbohydrate_g || 0).toFixed(1)),
          fat: parseFloat((r.grand_totals?.fat_g || 0).toFixed(1)),
          proteinPct: parseFloat((r.grand_totals?.percentage_protein || 0).toFixed(1)),
          carbsPct: parseFloat((r.grand_totals?.percentage_carbs || 0).toFixed(1)),
          fatPct: parseFloat((r.grand_totals?.percentage_fat || 0).toFixed(1)),
          // Route splits
          oralKcal: routeSplits.oralKcal,
          enteralKcal: routeSplits.enteralKcal,
          parenteralKcal: routeSplits.parenteralKcal,
          oralPct: routeSplits.oralPct,
          enteralPct: routeSplits.enteralPct,
          parenteralPct: routeSplits.parenteralPct,
          routeBadge,
          routeSplits,
        };
      });
  }, [records]);

  const getSmartBadge = (pct) => {
    const p = parseFloat(pct);
    if (p < 50) return { label: 'Defisit Berat', cls: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', icon: AlertCircle };
    if (p >= 80 && p <= 120) return { label: 'Asupan Adekuat', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle2 };
    if (p > 130) return { label: 'Waspada Overfeeding', cls: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500', icon: AlertTriangle };
    if (p >= 50 && p < 80) return { label: 'Perlu Peningkatan', cls: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: AlertTriangle };
    return { label: 'Mendekati Batas Atas', cls: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: AlertTriangle };
  };

  const getBarColor = (pct) => {
    if (pct < 50) return '#ef4444';
    if (pct >= 80 && pct <= 120) return '#10b981';
    if (pct > 130) return '#f43f5e';
    if (pct > 120) return '#f97316';
    return '#f59e0b';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    const badge = getSmartBadge(d.pct);
    const Icon = badge.icon;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs">
        <p className="font-semibold text-slate-800 mb-1">{d.fullDate}</p>
        <p className="text-slate-600">Energi: <strong>{d.energy} Kcal</strong></p>
        <p className="text-slate-600">Target: <strong>{d.target} Kcal</strong></p>
        <p className="text-slate-600">Pencapaian: <strong>{d.pct}%</strong></p>
        <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-medium ${badge.cls}`}>
          <Icon className="h-3 w-3" /> {badge.label}
        </span>
      </div>
    );
  };

  const MacroTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs min-w-[190px]">
        <p className="font-semibold text-slate-800 mb-1.5 pb-1 border-b border-slate-100">{d.fullDate}</p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-emerald-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
              Protein:
            </span>
            <span className="font-bold text-slate-800">
              {d.protein} g <span className="text-[10px] text-slate-400 font-normal">({d.proteinPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-purple-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
              Karbohidrat:
            </span>
            <span className="font-bold text-slate-800">
              {d.carbs} g <span className="text-[10px] text-slate-400 font-normal">({d.carbsPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-amber-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
              Lemak:
            </span>
            <span className="font-bold text-slate-800">
              {d.fat} g <span className="text-[10px] text-slate-400 font-normal">({d.fatPct}%)</span>
            </span>
          </div>
        </div>
        <div className="mt-2 pt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
          <span>Total Energi:</span>
          <span className="font-bold text-slate-800">{d.energy} Kcal</span>
        </div>
      </div>
    );
  };

  const RouteTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    const badge = d.routeBadge;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs min-w-[220px]">
        <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-slate-100 gap-2">
          <p className="font-semibold text-slate-800">{d.fullDate}</p>
          {badge && (
            <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold border ${badge.badgeClass}`}>
              {badge.label}
            </span>
          )}
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-emerald-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
              🥗 Oral:
            </span>
            <span className="font-bold text-slate-800">
              {d.oralKcal} Kcal <span className="text-[10px] text-slate-400 font-normal">({d.oralPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-sky-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0" />
              🥣 Enteral:
            </span>
            <span className="font-bold text-slate-800">
              {d.enteralKcal} Kcal <span className="text-[10px] text-slate-400 font-normal">({d.enteralPct}%)</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-purple-700 font-medium">
              <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
              💉 Parenteral:
            </span>
            <span className="font-bold text-slate-800">
              {d.parenteralKcal} Kcal <span className="text-[10px] text-slate-400 font-normal">({d.parenteralPct}%)</span>
            </span>
          </div>
        </div>
        <div className="mt-2 pt-1.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
          <span>Total Energi:</span>
          <span className="font-bold text-slate-800">{d.energy} Kcal</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Memuat profil pasien...</p>
        </div>
      </div>
    );
  }

  const avgBadge = stats ? getSmartBadge(stats.avgPct) : null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between">
      <div>
        <Navbar
          user={user}
          onLogout={() => { localStorage.removeItem('token'); navigate('/'); }}
          breadcrumbs={[
            { label: 'Daftar Pasien', href: '/patients' },
            { label: patient?.name || 'Pasien', href: patientId ? `/patients/${patientId}/calculate` : '/patients' },
            { label: 'Profil Ringkasan' }
          ]}
        />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 space-y-5 pb-16">

          {/* Header */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white font-black text-2xl flex-shrink-0">
                  {patient?.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-black text-white truncate">{patient?.name}</h1>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-purple-100 font-medium">
                    <span>{patient?.age ? `${patient.age} Tahun` : '-'}</span>
                    {patient?.gender && <><span>•</span><span>{patient.gender === 'male' ? 'Laki-laki' : 'Perempuan'}</span></>}
                    {patient?.diagnosis && <><span>•</span><span>{patient.diagnosis}</span></>}
                  </div>
                  {avgBadge && stats && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-white/20 backdrop-blur-sm text-white border-white/30">
                      <span className={`w-2 h-2 rounded-full ${avgBadge.dot} animate-pulse`}></span>
                      Rata-rata: {stats.avgPct}% Target &bull; {avgBadge.label}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => navigate(`/patients/${patientId}/calculate`)}
                className="h-9 text-xs font-semibold text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                <Activity className="h-3.5 w-3.5 mr-1.5" /> Buka Recall Hari Ini
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate(`/patients/${patientId}/history`)}
                className="h-9 text-xs font-semibold border-slate-200 hover:bg-slate-50">
                <Clock className="h-3.5 w-3.5 mr-1.5" /> Riwayat Recall
              </Button>
              <Button size="sm" variant="ghost" onClick={() => navigate('/patients')}
                className="h-9 text-xs font-medium text-slate-500 hover:text-slate-800 ml-auto">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Kembali
              </Button>
            </div>
          </div>

          {/* Stats & Charts */}
          {stats ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* Total Recall */}
                <Card className="border border-slate-200 shadow-xs bg-white">
                  <CardContent className="p-3.5 sm:p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Recall</p>
                      <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center"><BarChart2 className="h-3.5 w-3.5 text-purple-600" /></div>
                    </div>
                    <p className="text-2xl font-black text-slate-900">{stats.totalRecords}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 font-medium">catatan tersimpan</p>
                  </CardContent>
                </Card>
                {/* Avg % */}
                <Card className={`border shadow-xs ${parseFloat(stats.avgPct) >= 80 && parseFloat(stats.avgPct) <= 120 ? 'border-emerald-100 bg-emerald-50/60' : parseFloat(stats.avgPct) > 130 ? 'border-rose-100 bg-rose-50/60' : 'border-amber-100 bg-amber-50/60'}`}>
                  <CardContent className="p-3.5 sm:p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide">Rata-rata % Target</p>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${parseFloat(stats.avgPct) >= 80 && parseFloat(stats.avgPct) <= 120 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                        <Target className={`h-3.5 w-3.5 ${parseFloat(stats.avgPct) >= 80 && parseFloat(stats.avgPct) <= 120 ? 'text-emerald-600' : 'text-amber-600'}`} />
                      </div>
                    </div>
                    <p className="text-2xl font-black text-slate-900">{stats.avgPct}<span className="text-base font-semibold text-slate-400">%</span></p>
                    <p className="text-[11px] text-slate-400 mt-0.5 font-medium">dari target kalori</p>
                  </CardContent>
                </Card>
                {/* Avg Energy */}
                <Card className="border border-slate-200 shadow-xs bg-white">
                  <CardContent className="p-3.5 sm:p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide">Rata-rata Asupan</p>
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center"><Flame className="h-3.5 w-3.5 text-blue-500" /></div>
                    </div>
                    <p className="text-2xl font-black text-slate-900">{stats.avgEnergy}<span className="text-xs font-semibold text-slate-400"> Kcal</span></p>
                    <p className="text-[11px] text-slate-400 mt-0.5 font-medium">rata-rata harian</p>
                  </CardContent>
                </Card>
                {/* Adequate Days */}
                <Card className="border border-emerald-100 bg-emerald-50/60 shadow-xs">
                  <CardContent className="p-3.5 sm:p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide">Hari Adekuat</p>
                      <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /></div>
                    </div>
                    <p className="text-2xl font-black text-slate-900">{stats.adequateDays}<span className="text-base font-semibold text-slate-400">/{stats.totalRecords}</span></p>
                    <p className="text-[11px] text-slate-400 mt-0.5 font-medium">hari asupan 80–120%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Risk Banners */}
              {(stats.overfeedingDays > 0 || stats.deficitDays > 0) && (
                <div className="flex flex-wrap gap-3">
                  {stats.deficitDays > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-xs font-semibold text-red-700">
                      <AlertCircle className="h-4 w-4" />
                      <span>{stats.deficitDays} hari Defisit Berat (&lt;50%)</span>
                    </div>
                  )}
                  {stats.overfeedingDays > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-xs font-semibold text-rose-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{stats.overfeedingDays} hari Waspada Overfeeding (&gt;130%)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Trend Charts */}
              <Card className="border border-slate-200 shadow-xs bg-white">
                <CardHeader className="p-4 sm:p-5 pb-2">
                  <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-purple-600" />
                    Tren Asupan Kalori vs Target
                  </CardTitle>
                  <p className="text-xs text-slate-400 mt-0.5">Seluruh catatan recall pasien ini</p>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="h-48 sm:h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="energyGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                        <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="linear" dataKey="target" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 5" fill="none" name="Target" dot={false} />
                        <Area type="linear" dataKey="energy" stroke="#8b5cf6" strokeWidth={2} fill="url(#energyGrad)" name="Asupan" dot={{ r: 3, fill: '#8b5cf6', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-slate-600 mb-2 ml-1">Persentase Pencapaian Target (%)</p>
                    <div className="h-36 sm:h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" domain={[0, 'auto']} />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine y={80} stroke="#10b981" strokeDasharray="4 3" strokeWidth={1.5} label={{ value: '80%', position: 'right', fontSize: 9, fill: '#10b981' }} />
                          <ReferenceLine y={120} stroke="#f97316" strokeDasharray="4 3" strokeWidth={1.5} label={{ value: '120%', position: 'right', fontSize: 9, fill: '#f97316' }} />
                          <ReferenceLine y={130} stroke="#f43f5e" strokeDasharray="4 3" strokeWidth={1.5} label={{ value: '130%', position: 'right', fontSize: 9, fill: '#f43f5e' }} />
                          <Bar dataKey="pct" name="% Target" radius={[4, 4, 0, 0]}>
                            {chartData.map((entry, index) => (
                              <Cell key={index} fill={getBarColor(entry.pct)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 ml-1 text-[11px] text-slate-500 font-medium">
                      <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-emerald-500 inline-block"></span>Adekuat (80–120%)</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-amber-400 inline-block"></span>Perlu Peningkatan</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-rose-500 inline-block"></span>Overfeeding / Defisit</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tren Asupan Makronutrien (Protein, Karbohidrat & Lemak) */}
              <Card className="border border-slate-200 shadow-xs bg-white">
                <CardHeader className="p-4 sm:p-5 pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                        <Activity className="h-4 w-4 text-emerald-600" />
                        Tren Asupan Makronutrien (Protein, Karbohidrat & Lemak)
                      </CardTitle>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {macroView === 'grams' ? 'Asupan harian dalam gram (g)' : 'Distribusi kontribusi persentase energi (% Kcal)'}
                      </p>
                    </div>
                    {/* Toggle Gram / Persentase */}
                    <div className="flex items-center bg-slate-100 p-1 rounded-xl self-start sm:self-auto border border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => setMacroView('grams')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                          macroView === 'grams'
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Gram (g)
                      </button>
                      <button
                        type="button"
                        onClick={() => setMacroView('percentage')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                          macroView === 'percentage'
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Persentase (%)
                      </button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-3 sm:p-4 pt-2 space-y-4">
                  {/* Macro Chart */}
                  <div className="h-56 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      {macroView === 'grams' ? (
                        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" unit="g" />
                          <Tooltip content={<MacroTooltip />} />
                          <Line
                            type="linear"
                            dataKey="protein"
                            stroke="#10b981"
                            strokeWidth={2.5}
                            name="Protein (g)"
                            dot={{ r: 3.5, fill: '#10b981', strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                          />
                          <Line
                            type="linear"
                            dataKey="carbs"
                            stroke="#8b5cf6"
                            strokeWidth={2.5}
                            name="Karbohidrat (g)"
                            dot={{ r: 3.5, fill: '#8b5cf6', strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                          />
                          <Line
                            type="linear"
                            dataKey="fat"
                            stroke="#f59e0b"
                            strokeWidth={2.5}
                            name="Lemak (g)"
                            dot={{ r: 3.5, fill: '#f59e0b', strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      ) : (
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" unit="%" domain={[0, 100]} />
                          <Tooltip content={<MacroTooltip />} />
                          <Bar dataKey="proteinPct" name="% Protein" stackId="macro" fill="#10b981" />
                          <Bar dataKey="carbsPct" name="% Karbohidrat" stackId="macro" fill="#8b5cf6" />
                          <Bar dataKey="fatPct" name="% Lemak" stackId="macro" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 border-t border-slate-100">
                    {/* Protein */}
                    <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-100 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-8 rounded-full bg-emerald-500" />
                        <div>
                          <p className="text-[11px] font-bold text-emerald-800 uppercase tracking-wide">Protein</p>
                          <p className="text-xs text-slate-500">Rata-rata Harian</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-emerald-950">{stats.avgProtein} <span className="text-xs font-normal">g</span></p>
                        <p className="text-[10.5px] font-semibold text-emerald-700">{stats.avgProteinPct}% Kcal</p>
                      </div>
                    </div>

                    {/* Karbohidrat */}
                    <div className="p-3 rounded-xl bg-purple-50/70 border border-purple-100 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-8 rounded-full bg-purple-500" />
                        <div>
                          <p className="text-[11px] font-bold text-purple-800 uppercase tracking-wide">Karbohidrat</p>
                          <p className="text-xs text-slate-500">Rata-rata Harian</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-purple-950">{stats.avgCarbs} <span className="text-xs font-normal">g</span></p>
                        <p className="text-[10.5px] font-semibold text-purple-700">{stats.avgCarbsPct}% Kcal</p>
                      </div>
                    </div>

                    {/* Lemak */}
                    <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-100 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-8 rounded-full bg-amber-500" />
                        <div>
                          <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">Lemak</p>
                          <p className="text-xs text-slate-500">Rata-rata Harian</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-amber-950">{stats.avgFat} <span className="text-xs font-normal">g</span></p>
                        <p className="text-[10.5px] font-semibold text-amber-700">{stats.avgFatPct}% Kcal</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tren Komparasi Rute Asupan (Oral vs Enteral vs Parenteral Split) */}
              <Card className="border border-slate-200 shadow-xs bg-white">
                <CardHeader className="p-4 sm:p-5 pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                          <Layers className="h-4 w-4 text-sky-600" />
                          Tren Komparasi Rute Asupan (Oral vs Enteral vs Parenteral)
                        </CardTitle>
                        {stats.latestRouteBadge && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${stats.latestRouteBadge.badgeClass}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${stats.latestRouteBadge.dotClass}`} />
                            {stats.latestRouteBadge.label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {routeView === 'calories' ? 'Distribusi energi harian per rute dalam Kcal' : 'Proporsi kontribusi persentase per rute (% total asupan)'}
                      </p>
                    </div>
                    {/* Toggle Kkal / Persentase */}
                    <div className="flex items-center bg-slate-100 p-1 rounded-xl self-start sm:self-auto border border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => setRouteView('calories')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                          routeView === 'calories'
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Kalori (Kkal)
                      </button>
                      <button
                        type="button"
                        onClick={() => setRouteView('percentage')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                          routeView === 'percentage'
                            ? 'bg-white text-slate-900 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Proporsi (%)
                      </button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-3 sm:p-4 pt-2 space-y-4">
                  {/* Route Chart */}
                  <div className="h-56 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      {routeView === 'calories' ? (
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" unit=" Kcal" />
                          <Tooltip content={<RouteTooltip />} />
                          <Bar dataKey="oralKcal" name="Oral (Kcal)" stackId="route" fill="#10b981" />
                          <Bar dataKey="enteralKcal" name="Enteral (Kcal)" stackId="route" fill="#0284c7" />
                          <Bar dataKey="parenteralKcal" name="Parenteral (Kcal)" stackId="route" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                          <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" unit="%" domain={[0, 100]} />
                          <Tooltip content={<RouteTooltip />} />
                          <Bar dataKey="oralPct" name="% Oral" stackId="route" fill="#10b981" />
                          <Bar dataKey="enteralPct" name="% Enteral" stackId="route" fill="#0284c7" />
                          <Bar dataKey="parenteralPct" name="% Parenteral" stackId="route" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>

                  {/* Cumulative Split Bar */}
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                        <GitFork className="h-3.5 w-3.5 text-sky-600" />
                        Proporsi Kumulatif Rute Asupan:
                      </span>
                      <span className="text-[11px] text-slate-400 font-medium">
                        Total {stats.totalOralKcal + stats.totalEnteralKcal + stats.totalParenteralKcal} Kcal seluruh perawatan
                      </span>
                    </div>

                    <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden flex">
                      {stats.cumOralPct > 0 && (
                        <div
                          style={{ width: `${stats.cumOralPct}%` }}
                          className="bg-emerald-500 h-full transition-all"
                          title={`Oral: ${stats.cumOralPct}%`}
                        />
                      )}
                      {stats.cumEnteralPct > 0 && (
                        <div
                          style={{ width: `${stats.cumEnteralPct}%` }}
                          className="bg-sky-500 h-full transition-all"
                          title={`Enteral: ${stats.cumEnteralPct}%`}
                        />
                      )}
                      {stats.cumParenteralPct > 0 && (
                        <div
                          style={{ width: `${stats.cumParenteralPct}%` }}
                          className="bg-purple-500 h-full transition-all"
                          title={`Parenteral: ${stats.cumParenteralPct}%`}
                        />
                      )}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-600 pt-0.5">
                      <span className="flex items-center gap-1.5 font-medium">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        🥗 Oral: <strong>{stats.cumOralPct}%</strong> ({stats.totalOralKcal} Kcal)
                      </span>
                      <span className="flex items-center gap-1.5 font-medium">
                        <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
                        🥣 Enteral: <strong>{stats.cumEnteralPct}%</strong> ({stats.totalEnteralKcal} Kcal)
                      </span>
                      <span className="flex items-center gap-1.5 font-medium">
                        <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                        💉 Parenteral: <strong>{stats.cumParenteralPct}%</strong> ({stats.totalParenteralKcal} Kcal)
                      </span>
                    </div>
                  </div>

                  {/* 3 Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 border-t border-slate-100">
                    {/* Oral */}
                    <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-100 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-8 rounded-full bg-emerald-500" />
                        <div>
                          <p className="text-[11px] font-bold text-emerald-800 uppercase tracking-wide">🥗 Rute Oral</p>
                          <p className="text-xs text-slate-500">Rata-rata Harian</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-emerald-950">{stats.avgOralKcal} <span className="text-xs font-normal">Kcal</span></p>
                        <p className="text-[10.5px] font-semibold text-emerald-700">{stats.cumOralPct}% Energi</p>
                      </div>
                    </div>

                    {/* Enteral */}
                    <div className="p-3 rounded-xl bg-sky-50/70 border border-sky-100 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-8 rounded-full bg-sky-500" />
                        <div>
                          <p className="text-[11px] font-bold text-sky-800 uppercase tracking-wide">🥣 Rute Enteral</p>
                          <p className="text-xs text-slate-500">Rata-rata Harian</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-sky-950">{stats.avgEnteralKcal} <span className="text-xs font-normal">Kcal</span></p>
                        <p className="text-[10.5px] font-semibold text-sky-700">{stats.cumEnteralPct}% Energi</p>
                      </div>
                    </div>

                    {/* Parenteral */}
                    <div className="p-3 rounded-xl bg-purple-50/70 border border-purple-100 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-8 rounded-full bg-purple-500" />
                        <div>
                          <p className="text-[11px] font-bold text-purple-800 uppercase tracking-wide">💉 Rute Parenteral</p>
                          <p className="text-xs text-slate-500">Rata-rata Harian</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-purple-950">{stats.avgParenteralKcal} <span className="text-xs font-normal">Kcal</span></p>
                        <p className="text-[10.5px] font-semibold text-purple-700">{stats.cumParenteralPct}% Energi</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card className="border border-slate-200 shadow-xs bg-white">
                <CardHeader className="p-4 sm:p-5 pb-2">
                  <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    Timeline Recall ({records.length} Catatan)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {[...records]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((record, idx) => {
                        const pct = record.target_energy_kcal > 0
                          ? Math.round((record.grand_totals.energy_kcal / record.target_energy_kcal) * 100) : 0;
                        const badge = getSmartBadge(pct);
                        const Icon = badge.icon;
                        const d = new Date(record.date);
                        const recordRoute = calculateRouteSplits(record.oral_enteral_items, record.parenteral_items);
                        return (
                          <div key={record.id || idx}
                            className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/60 transition-colors cursor-pointer group"
                            onClick={() => navigate(`/patients/${patientId}/history`)}>
                            <div className="flex-shrink-0 text-center w-9">
                              <p className="text-lg font-black text-slate-800 leading-none">{String(d.getDate()).padStart(2, '0')}</p>
                              <p className="text-[10px] font-semibold text-slate-400 uppercase">{d.toLocaleDateString('id-ID', { month: 'short' })}</p>
                              <p className="text-[10px] text-slate-300">{d.getFullYear()}</p>
                            </div>
                            <div className="w-px h-12 bg-slate-200 flex-shrink-0"></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-slate-900">{Math.round(record.grand_totals.energy_kcal)} Kcal</span>
                                <span className="text-[10px] text-slate-400">/ {record.target_energy_kcal} Kcal target</span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-slate-500">
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${badge.cls}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${badge.dot} flex-shrink-0`}></span>
                                  {pct}% &bull; {badge.label}
                                </span>
                                <span className="text-slate-300 hidden sm:inline">•</span>
                                <span className="text-emerald-700 font-medium">P: {formatNumber(record.grand_totals?.protein_g || 0)}g</span>
                                <span className="text-slate-300 hidden sm:inline">•</span>
                                <span className="text-purple-700 font-medium">KH: {formatNumber(record.grand_totals?.carbohydrate_g || 0)}g</span>
                                <span className="text-slate-300 hidden sm:inline">•</span>
                                <span className="text-amber-700 font-medium">L: {formatNumber(record.grand_totals?.fat_g || 0)}g</span>
                              </div>
                              {/* Rincian Rute Asupan */}
                              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[10px]">
                                {recordRoute.oralKcal > 0 && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200/50">
                                    🥗 Oral: {recordRoute.oralKcal} Kcal ({recordRoute.oralPct}%)
                                  </span>
                                )}
                                {recordRoute.enteralKcal > 0 && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-sky-50 text-sky-800 font-semibold border border-sky-200/50">
                                    🥣 Enteral: {recordRoute.enteralKcal} Kcal ({recordRoute.enteralPct}%)
                                  </span>
                                )}
                                {recordRoute.parenteralKcal > 0 && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-purple-50 text-purple-800 font-semibold border border-purple-200/50">
                                    💉 Parenteral: {recordRoute.parenteralKcal} Kcal ({recordRoute.parenteralPct}%)
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors" />
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border border-dashed border-slate-200 bg-white shadow-xs">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                  <Activity className="h-7 w-7 text-slate-400" />
                </div>
                <p className="font-semibold text-slate-700">Belum Ada Recall</p>
                <p className="text-xs text-slate-400 mt-1">Pasien ini belum memiliki catatan recall nutrisi</p>
                <Button size="sm" className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => navigate(`/patients/${patientId}/calculate`)}>
                  <Activity className="h-3.5 w-3.5 mr-1.5" /> Mulai Input Recall
                </Button>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
      <Footer />
      <BackToTop />
    </div>
  );
}
